// Project Quote Log — P3-A/P3-B (docs/implementation/P3_QUOTES_PRICING_INTELLIGENCE.md,
// audit in docs/implementation/P3_AUDIT_2026-09-12.md). Pure helpers over the
// rows sp_GetProjectQuotes (db/71) returns plus the project's bid-package spec
// sections — nothing here is stored.
//
// Flags reuse the gap-report rules (db/31: no ScopeAreaSF, no line detail) and
// add staleness (expired / expiring / stale) and bid-log state (P3-B: not
// reviewed, excluded, withdrawn, superseded). Coverage is the structured join
// the audit identified: an in-scope spec section names a cost code; a quote's
// Category anchors to a cost code (QuoteCategoryOptions, db/33); matching the
// two says which scope has quotes and which has none — derived from canonical
// rows, never a maintained checklist. Quotes whose category maps to no
// in-scope section are surfaced too, never dropped. A quote is LIVE for
// coverage when it is not expired, excluded, withdrawn or superseded.

import { DEFAULT_REVIEW_STATUS } from "../config/quoteStatus";

const DAY = 86400000;
const num = (v) => { if (v === null || v === undefined || v === "") return null; const n = Number(v); return Number.isFinite(n) ? n : null; };
const dateOf = (v) => { if (!v) return null; const d = new Date(String(v).length === 10 ? `${v}T00:00:00` : v); return isNaN(d) ? null : d; };

export const EXPIRING_SOON_DAYS = 14;
export const STALE_QUOTE_DAYS = 180;
// Flags that take a quote out of coverage.
export const NOT_LIVE_CODES = new Set(["expired", "excluded", "withdrawn", "superseded"]);

// Flags for one quote. `lineCount` may come from the row (LineItemCount) or be
// passed; `supersededBy` is the newer quote's id when another row's
// SupersedesQuoteID points here (computed by attachFlags across the set).
//   codes: expired | expiring_soon | stale_quote | no_line_detail | no_scope_area | no_vendor |
//          no_cost_code | not_reviewed | excluded | withdrawn | superseded | due_passed
export function quoteFlags(quote, { today = new Date(), lineCount, supersededBy = null } = {}) {
  const flags = [];
  const lines = lineCount ?? num(quote.LineItemCount) ?? 0;
  const valid = dateOf(quote.ValidUntil);
  if (valid) {
    const diff = (valid - today) / DAY;
    if (diff < 0) { const ago = Math.floor(-diff); flags.push({ code: "expired", severity: "high", message: `Quote validity ended ${ago} day${ago === 1 ? "" : "s"} ago (${quote.ValidUntil}).` }); }
    else { const days = Math.ceil(diff); if (days <= EXPIRING_SOON_DAYS) flags.push({ code: "expiring_soon", severity: "medium", message: `Quote validity ends in ${days} day${days === 1 ? "" : "s"} (${quote.ValidUntil}).` }); }
  }
  const quoted = dateOf(quote.QuoteDate);
  if (!valid && quoted && (today - quoted) / DAY > STALE_QUOTE_DAYS) {
    flags.push({ code: "stale_quote", severity: "medium", message: `No validity date and the quote is over ${STALE_QUOTE_DAYS} days old (${quote.QuoteDate}).` });
  }
  if (quote.QuoteStatus === "withdrawn") flags.push({ code: "withdrawn", severity: "high", message: "Withdrawn by the vendor — kept for the record, not counted toward coverage." });
  if (supersededBy != null) flags.push({ code: "superseded", severity: "medium", message: `Superseded by quote #${supersededBy} — kept as history, not counted toward coverage.` });
  const review = quote.ReviewStatus || DEFAULT_REVIEW_STATUS;
  if (review === "excluded") flags.push({ code: "excluded", severity: "medium", message: "Excluded by the estimator — not to be used on this project." });
  else if (review === "unreviewed") flags.push({ code: "not_reviewed", severity: "medium", message: "Received but not yet reviewed by an estimator." });
  if (lines === 0) flags.push({ code: "no_line_detail", severity: "medium", message: "Saved as a lump sum — no line items, so it cannot be leveled or normalized." });
  if (num(quote.ScopeAreaSF) === null) flags.push({ code: "no_scope_area", severity: "low", message: "No scope area (SF) — no $/SF is derivable." });
  if (!quote.VendorName && quote.VendorID == null) flags.push({ code: "no_vendor", severity: "high", message: "No vendor on the quote." });
  if (quote.CostCodeID == null) flags.push({ code: "no_cost_code", severity: "low", message: `Category "${quote.Category || "—"}" is not anchored to a cost code (Quote Log Setup) — it cannot be matched to a spec section.` });
  const due = dateOf(quote.DueDate), received = dateOf(quote.ReceivedDate) || quoted;
  if (due && received && received > due) flags.push({ code: "due_passed", severity: "low", message: `Received after the due date (${quote.DueDate}).` });
  return flags;
}

export const isLive = (flaggedQuote) => !flaggedQuote.flags.some(f => NOT_LIVE_CODES.has(f.code));

// Decorate a set of quotes; superseded-by is resolved across the set.
export function attachFlags(quotes = [], { today = new Date() } = {}) {
  const supersededBy = new Map();
  for (const q of quotes) if (q.SupersedesQuoteID != null) supersededBy.set(q.SupersedesQuoteID, q.QuoteID);
  return quotes.map(q => ({ ...q, flags: quoteFlags(q, { today, supersededBy: supersededBy.get(q.QuoteID) ?? null }) }));
}

// Group flagged quotes by Category (the quote log's reading order).
export function groupQuotes(quotes = []) {
  const groups = new Map();
  for (const q of quotes) {
    const key = q.Category || "Uncategorized";
    const g = groups.get(key) || { category: key, costCodeId: q.CostCodeID ?? null, quotes: [] };
    if (g.costCodeId == null && q.CostCodeID != null) g.costCodeId = q.CostCodeID;
    g.quotes.push(q);
    groups.set(key, g);
  }
  return [...groups.values()].sort((a, b) => a.category.localeCompare(b.category));
}

// Bid coverage: in-scope spec sections (cost codes) × quotes whose category
// anchors to the same cost code.
//   status: 'covered' (≥1 live reviewed quote) | 'unreviewed_only' (live quotes exist but none reviewed) |
//           'expired_only' (quotes exist, none live) | 'uncovered' | 'no_cost_code'
export function buildCoverage(specSections = [], flaggedQuotes = []) {
  const inScope = specSections.filter(s => s.InScope !== false && s.InScope !== 0);
  const byCostCode = new Map();
  for (const q of flaggedQuotes) if (q.CostCodeID != null) (byCostCode.get(q.CostCodeID) || byCostCode.set(q.CostCodeID, []).get(q.CostCodeID)).push(q);
  const matchedQuoteIds = new Set();
  const sections = inScope.map(s => {
    const quotes = s.CostCodeID != null ? (byCostCode.get(s.CostCodeID) || []) : [];
    for (const q of quotes) matchedQuoteIds.add(q.QuoteID);
    const live = quotes.filter(isLive);
    const reviewed = live.filter(q => q.ReviewStatus === "reviewed");
    const status = s.CostCodeID == null ? "no_cost_code" : quotes.length === 0 ? "uncovered" : live.length === 0 ? "expired_only" : reviewed.length === 0 ? "unreviewed_only" : "covered";
    return { specSectionId: s.SpecSectionID, csiCode: s.CSICode, title: s.Title, costCodeId: s.CostCodeID ?? null, quotes, liveQuoteCount: live.length, reviewedQuoteCount: reviewed.length, status };
  });
  const unmatchedQuotes = flaggedQuotes.filter(q => !matchedQuoteIds.has(q.QuoteID));
  const count = (st) => sections.filter(s => s.status === st).length;
  return {
    sections, unmatchedQuotes,
    summary: { sections: sections.length, covered: count("covered"), unreviewedOnly: count("unreviewed_only"), uncovered: count("uncovered"), expiredOnly: count("expired_only"), noCostCode: count("no_cost_code"), unmatched: unmatchedQuotes.length },
  };
}

// "Cost / X" snapshot rows for one quote, as display strings.
export function snapshotSummaries(quoteId, snapshots = []) {
  return snapshots.filter(s => s.QuoteID === quoteId && num(s.UnitCost) !== null)
    .map(s => ({ label: s.MetricLabel, text: `$${num(s.UnitCost).toLocaleString("en-US", { maximumFractionDigits: 2 })} / ${s.UnitLabel || s.MetricLabel.replace(/^Cost \/ /, "")}` }));
}

// ---- sp_UpdateQuote payloads (P3-B) ---------------------------------------------------------
// sp_UpdateQuote writes the bid-log fields ONLY when the key is present
// (JSON_PATH_EXISTS), so a patch must carry exactly the keys being changed —
// never the whole row — or an unrelated save would blank a field.

export const BID_LOG_FIELDS = { QuoteStatus: "quote_status", ReviewStatus: "review_status", QuoteVersion: "quote_version", SupersedesQuoteID: "supersedes_quote_id", RequestedDate: "requested_date", DueDate: "due_date", ProjectID: "project_id" };

export function quoteFieldPatch(quoteId, patch = {}) {
  const out = { quote_id: quoteId };
  for (const [col, key] of Object.entries(BID_LOG_FIELDS)) {
    if (Object.prototype.hasOwnProperty.call(patch, col)) out[key] = patch[col] === "" ? null : patch[col];
  }
  if (Object.keys(out).length === 1) throw new Error("quoteFieldPatch: nothing to change");
  return out;
}
export const attachQuotePayload = (quoteId, projectId) => quoteFieldPatch(quoteId, { ProjectID: projectId });
export const detachQuotePayload = (quoteId) => quoteFieldPatch(quoteId, { ProjectID: null });
